import React from 'react';
import {
  View,
  Text,
  Image,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  ViewStyle,
  Alert,
} from 'react-native';
import { Product } from '../types';
import { useApp } from '../context/AppContext';
import { usePersistentCart } from '../context/PersistentCartContext';
import { useFavorites } from '../context/FavoritesContext';

interface ProductCardProps {
  product: Product;
  onPress: () => void;
  onFavoritePress?: () => void;
  style?: ViewStyle;
}

const { width } = Dimensions.get('window');
const cardWidth = (width - 48) / 2; // 2 columns with padding

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  onPress,
  onFavoritePress,
  style,
}): React.ReactElement => {
  const { favoriteProducts } = useApp();
  const { addToCart, state } = usePersistentCart();
  const { isFavorite, addToFavorites, removeFromFavorites } = useFavorites();
  
  // Check if product is in cart
  const productInCart = state.items.some(item => item.id_producto === product.id);

  const handleFavoritePress = async (e: any) => {
    e.stopPropagation(); // Evitar que se active onPress del card
    
    try {
      if (isFavorite(product.id)) {
        await removeFromFavorites(product.id);
        console.log('💔 Producto removido de favoritos:', product.nombre);
      } else {
        await addToFavorites(product);
        console.log('❤️ Producto agregado a favoritos:', product.nombre);
      }
    } catch (error) {
      console.error('❌ Error al manejar favorito:', error);
      Alert.alert('Error', 'No se pudo actualizar favoritos');
    }
  };

  const formatPrice = (price: string) => {
    const numPrice = parseFloat(price);
    return `Q${numPrice.toLocaleString('es-GT', { minimumFractionDigits: 2 })}`;
  };

  const getStockStatus = (stock: number) => {
    if (stock === 0) return { text: 'Agotado', color: '#FF3B30' };
    if (stock <= 3) return { text: `Solo ${stock}`, color: '#FF9500' };
    return { text: 'Disponible', color: '#34C759' };
  };

  const handleAddToCart = async (e: any) => {
    e.stopPropagation(); // Evitar que se active onPress del card
    
    if (product.stock === 0) {
      Alert.alert('Sin stock', 'Este producto no está disponible');
      return;
    }

    try {
      // Convert product to the format expected by PersistentCart
      const cartProduct = {
        id: product.id,
        nombre: product.nombre,
        precio: parseFloat(product.precio), // Convert string to number
        imagen: product.imagen,
        stock: product.stock
      };
      
      await addToCart(cartProduct, 1);
      Alert.alert('¡Agregado!', `${product.nombre} agregado al carrito`);
    } catch (error) {
      console.error('🛒 Error al agregar al carrito:', error);
      Alert.alert('Error', 'No se pudo agregar el producto al carrito');
    }
  };

  const stockStatus = getStockStatus(product.stock);

  return (
    <TouchableOpacity style={[styles.card, style]} onPress={onPress} activeOpacity={0.8}>
      <View style={styles.imageContainer}>
        <Image
          source={{ 
            uri: product.imagen || 'https://via.placeholder.com/200x150.jpg?text=Sin+Imagen' 
          }}
          style={styles.image}
          resizeMode="cover"
        />
        
        <TouchableOpacity
          style={styles.favoriteButton}
          onPress={handleFavoritePress}
          activeOpacity={0.8}
        >
          <Text style={styles.favoriteIcon}>
            {isFavorite(product.id) ? '❤️' : '🤍'}
          </Text>
        </TouchableOpacity>
        
        <View style={styles.categoryBadge}>
          <Text style={styles.categoryText}>{product.categoria}</Text>
        </View>
      </View>
      
      <View style={styles.content}>
        <Text style={styles.title} numberOfLines={2}>
          {product.nombre}
        </Text>
        
        <Text style={styles.price}>
          {formatPrice(product.precio)}
        </Text>
        
        <View style={styles.footer}>
          <View style={styles.stockContainer}>
            <View style={[styles.stockDot, { backgroundColor: stockStatus.color }]} />
            <Text style={[styles.stockText, { color: stockStatus.color }]}>
              {stockStatus.text}
            </Text>
          </View>
          
          {product.empresa && (
            <Text style={styles.seller} numberOfLines={1}>
              {product.empresa.name}
            </Text>
          )}
        </View>

        {/* Botón agregar al carrito */}
        <TouchableOpacity 
          style={[
            styles.addToCartButton,
            product.stock === 0 && styles.addToCartButtonDisabled,
            productInCart && styles.addToCartButtonInCart
          ]}
          onPress={handleAddToCart}
          disabled={product.stock === 0}
          activeOpacity={0.8}
        >
          <Text style={[
            styles.addToCartButtonText,
            product.stock === 0 && styles.addToCartButtonTextDisabled,
            productInCart && styles.addToCartButtonTextInCart
          ]}>
            {product.stock === 0 ? 'Sin Stock' : 
             productInCart ? '✓ En Carrito' : '🛒 Agregar'}
          </Text>
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    width: cardWidth,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    marginBottom: 16,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  imageContainer: {
    position: 'relative',
    width: '100%',
    height: 120,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  favoriteButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  favoriteIcon: {
    fontSize: 16,
  },
  categoryBadge: {
    position: 'absolute',
    bottom: 8,
    left: 8,
    backgroundColor: 'rgba(0, 122, 255, 0.9)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  categoryText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  content: {
    padding: 12,
  },
  title: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333333',
    marginBottom: 4,
    lineHeight: 18,
  },
  price: {
    fontSize: 16,
    fontWeight: '700',
    color: '#007AFF',
    marginBottom: 8,
  },
  footer: {
    flexDirection: 'column',
    gap: 4,
  },
  stockContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  stockDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginRight: 6,
  },
  stockText: {
    fontSize: 12,
    fontWeight: '600',
  },
  seller: {
    fontSize: 11,
    color: '#666666',
    fontStyle: 'italic',
  },
  addToCartButton: {
    backgroundColor: '#e74c3c',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 8,
  },
  addToCartButtonDisabled: {
    backgroundColor: '#cccccc',
  },
  addToCartButtonInCart: {
    backgroundColor: '#27ae60',
  },
  addToCartButtonText: {
    color: 'white',
    fontSize: 12,
    fontWeight: 'bold',
  },
  addToCartButtonTextDisabled: {
    color: '#888888',
  },
  addToCartButtonTextInCart: {
    color: 'white',
  },
});

export default ProductCard;
